import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import { Pencil, Trash2, Eye, Search, Download, ChevronDown, ChevronRight, X, RotateCcw } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import AddClient from "./AddClient";
import ThemedTable from "../../components/ThemedTable";
import Loader from "../../components/Loader";
import ConfirmDialog from "../../components/ConfirmDialog";
const Client = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // For delete confirmation modal
  const [deleteClientObj, setDeleteClientObj] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [expandedRowId, setExpandedRowId] = useState(null);

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const toggleRow = (id) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  /* ================= FETCH CLIENTS ================= */
  /* ================= FETCH CLIENTS & HIGHLIGHT ================= */
  const location = useLocation();
  const highlightId = new URLSearchParams(location.search).get("highlight");

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (highlightId && clients.length > 0) {
      setExpandedRowId(highlightId);
      setTimeout(() => {
        const element = document.getElementById(`client-row-${highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [highlightId, clients]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/clients");
      setClients(res.data?.clients || []);
    } catch {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const downloadClientExcel = async () => {
    try {
      setIsDownloadingExcel(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (cityFilter !== "all") params.append("city", cityFilter);

      const res = await apiClient.get(`/clients/export/excel?${params.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ClientsList.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel report downloaded successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export client report");
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const clientCities = useMemo(() => {
    const cities = new Set();
    clients.forEach((c) => {
      if (c.city) cities.add(c.city.trim());
    });
    return Array.from(cities).sort();
  }, [clients]);

  /* ================= FILTER ================= */
  const filteredClients = clients.filter((c) => {
    const matchesSearch =
      !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.phone?.includes(search) ||
      c.city?.toLowerCase().includes(search.toLowerCase());

    const matchesCity =
      cityFilter === "all" || (c.city && c.city.trim() === cityFilter);

    const matchesType =
      typeFilter === "all" ||
      (typeFilter === "wholesale" && c.businessType === "wholesale") ||
      (typeFilter === "gym" && c.businessType === "gym") ||
      (typeFilter === "unspecified" && (!c.businessType || c.businessType.trim() === ""));

    return matchesSearch && matchesCity && matchesType;
  });

  /* ================= HELPERS ================= */
  const renderBalance = (balance = 0) => {
    const formattedBalance = Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    if (balance > 0)
      return <span className="text-red-600 font-semibold">₹ {formattedBalance} Dr</span>;

    if (balance < 0)
      return (
        <span className="text-green-600 font-semibold">
          ₹ {formattedBalance} Cr
        </span>
      );

    return <span className="text-slate-500">₹ 0.00</span>;
  };

  /* ================= EDIT ================= */
  const handleEdit = (client) => {
    setSelectedClient(client);
    setIsEdit(true);
    setIsAddClientOpen(true);
  };

  /* ================= DELETE ================= */
  const handleDelete = (client) => {
    setDeleteClientObj(client);
  };

  const confirmDeleteClient = async () => {
    if (!deleteClientObj) return;

    setIsDeleting(true);
    try {
      await apiClient.delete(`/clients/${deleteClientObj._id}`);
      setClients((prev) => prev.filter((c) => c._id !== deleteClientObj._id));
      toast.success("Client deleted successfully");
      setDeleteClientObj(null);
    } catch {
      toast.error("Failed to delete client");
    } finally {
      setIsDeleting(false);
    }
  };

  /* ================= ADD / UPDATE CALLBACK ================= */
  const handleAddClient = (client, editMode) => {
    if (editMode) {
      setClients((prev) =>
        prev.map((c) => (c._id === client._id ? client : c)),
      );
    } else {
      setClients((prev) => [...prev, client]);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-0">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl font-bold text-slate-800">Clients</h1>
            <span className="bg-orange-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full shadow-sm">
              {search || cityFilter !== "all"
                ? `Showing ${filteredClients.length} of ${clients.length}`
                : `${clients.length} Total`}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Manage clients and view balances
          </p>
        </div>
        <div className="flex items-center gap-3 me-4">
          <button
            onClick={downloadClientExcel}
            disabled={isDownloadingExcel}
            className="px-3.5 py-2 bg-[#172554] text-white rounded-md hover:bg-[#1e2f6a] transition-all flex items-center justify-center gap-2 text-sm font-medium active:scale-95 disabled:opacity-50"
            title="Export Excel"
          >
            {isDownloadingExcel ? (
              <Loader inline={true} text="" />
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Export Excel</span>
              </>
            )}
          </button>

          <button
            onClick={() => {
              setIsAddClientOpen(true);
              setIsEdit(false);
              setSelectedClient(null);
            }}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
          >
            Add Client
          </button>
        </div>
      </div>

      {/* ================= FILTER BAR ================= */}
      <div className="bg-white border-b border-slate-200">
        <div className="flex flex-col sm:flex-row gap-2 w-full items-stretch p-4">
          {/* SEARCH */}
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search size={16} />
            </div>
            <input
              type="text"
              placeholder="Search clients by name, email, phone, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10 py-2 w-full text-sm bg-white border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm text-slate-800"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* CITY FILTER */}
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="w-full sm:w-44 px-3 py-2 text-sm bg-white border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition text-slate-700 font-medium shadow-sm"
          >
            <option value="all">All Cities</option>
            {clientCities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>

          {/* CLIENT TYPE FILTER */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full sm:w-44 px-3 py-2 text-sm bg-white border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition text-slate-700 font-medium shadow-sm"
          >
            <option value="all">All Types</option>
            <option value="wholesale">Wholesale Shop</option>
            <option value="gym">Gym Owner</option>
            <option value="unspecified">Unspecified Type</option>
          </select>

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSearch("");
                setCityFilter("all");
                setTypeFilter("all");
              }}
              className="w-9 h-9 bg-[#172554] text-white rounded-md border border-[#172554] hover:bg-[#1e2f6a] transition-all flex items-center justify-center shadow-sm shrink-0 active:scale-95"
              title="Reset Filters"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-slate-200 rounded-none bg-white flex-1 min-h-0 flex flex-col border-x-0 border-b-0">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <ThemedTable className="text-left border-collapse w-full">
            <thead className="sticky top-0 z-20 bg-white shadow-sm">
              <tr className="bg-white text-gray-700">
                <th className="p-3 w-10 text-center"></th>
                <th className="p-3">Name</th>
                <th className="p-3 hidden sm:table-cell whitespace-nowrap">Phone</th>
                <th className="p-3 hidden md:table-cell">City</th>
               
                <th className="p-3 hidden xl:table-cell text-center whitespace-nowrap">
                  Last Order Date
                </th>
                
                <th className="p-3 text-center whitespace-nowrap">
                  Balance
                </th>
                <th className="p-3 w-[120px] text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* LOADER */}
              {loading ? (
                <tr>
                  <td colSpan="8" className="py-10 text-center">
                    <Loader text="Loading clients..." size={32} />
                  </td>
                </tr>
              ) : filteredClients.length > 0 ? (
                filteredClients.map((client, idx) => (
                  <React.Fragment key={client._id}>
                    <tr
                      id={`client-row-${client._id}`}
                      className={`border-b border-slate-100 transition cursor-pointer ${
                        expandedRowId === client._id
                          ? highlightId === client._id 
                            ? "bg-amber-100 ring-2 ring-amber-400" 
                            : "bg-blue-50"
                          : "hover:bg-slate-50"
                      } h-[60px]`}
                      onClick={() => toggleRow(client._id)}
                    >
                      <td className="p-3 text-slate-400 text-center">
                        {expandedRowId === client._id ? (
                          <ChevronDown size={16} className="text-blue-500 mx-auto" />
                        ) : (
                          <ChevronRight size={16} className="mx-auto" />
                        )}
                      </td>
                      <td className="p-3 font-medium capitalize break-words">
                         <div className="flex items-center flex-wrap gap-2">
                           <span>{client.name}</span>
                           {client.businessType === "gym" && (
                             <span className="text-[9px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                               Gym
                             </span>
                           )}
                           {client.businessType === "wholesale" && (
                             <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                               Wholesale
                             </span>
                           )}
                         </div>
                       </td>
                      <td className="p-3 hidden sm:table-cell whitespace-nowrap">
                        {client.phone}
                      </td>
                      <td className="p-3 hidden md:table-cell capitalize">
                        {client.city || "-"}
                      </td>
                      
                     
                   
                      <td className="p-3 hidden xl:table-cell text-center whitespace-nowrap text-slate-500">
                        {client.lastOrderedDate ? new Date(client.lastOrderedDate).toLocaleDateString() : "-"}
                      </td>
                    
                      <td className="p-3 text-center whitespace-nowrap">
                        {renderBalance(client.balance)}
                      </td>
                      <td className="p-3 text-center whitespace-nowrap">
                        <div className="flex gap-2 justify-center items-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(client);
                            }}
                            className="p-1.5 sm:p-2 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/manage-client/client-ledger/${client._id}`,
                              );
                            }}
                            className="p-1.5 sm:p-2 rounded-full border-2 border-[#172554] text-white bg-[#172554] hover:bg-white hover:text-[#172554] transition"
                            title="View Ledger"
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          {user?.role === 'superAdmin' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(client);
                              }}
                              className="p-1.5 sm:p-2 rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRowId === client._id && (
                      <tr className="bg-white border-b border-slate-200">
                        <td colSpan="12" className="p-0">
                          <div className="flex flex-col lg:flex-row gap-0">
                            {/* Left Column: Metadata */}
                            <div className="flex-1 flex flex-col justify-start">
                              <div className="bg-slate-100/70 text-slate-600 px-4 py-2 border-y border-slate-200/60 font-bold uppercase text-[10px] tracking-wider">
                                Client Profile & Stats
                              </div>
                              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-8 text-xs">
                                {/* Profile Column */}
                                <div className="space-y-2.5">
                                  {client.businessType && (
                                    <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                      <span>Client Type:</span>{" "}
                                      <span className="font-semibold text-right capitalize">
                                        {client.businessType === "gym" ? "Gym Owner" : "Wholesale Store"}
                                      </span>
                                    </p>
                                  )}
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Company:</span>{" "}
                                    <span className="font-semibold text-right">{client.companyName || "-"}</span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Email:</span>{" "}
                                    <span className="font-semibold text-right">{client.email || "-"}</span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Phone:</span>{" "}
                                    <span className="font-semibold text-right">{client.phone || "-"}</span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>City / State:</span>{" "}
                                    <span className="font-semibold capitalize text-right">
                                      {client.city ? client.city : "-"} {client.state ? `, ${client.state}` : ""}
                                    </span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Country / Zip:</span>{" "}
                                    <span className="font-semibold capitalize text-right">
                                      {client.country ? client.country : "-"} {client.zipCode ? ` (${client.zipCode})` : ""}
                                    </span>
                                  </p>
                                  <div className="text-slate-600 flex justify-between pt-1">
                                    <span className="min-w-[70px]">Address:</span>{" "}
                                    <span className="font-semibold capitalize text-right whitespace-normal break-words">{client.address || "-"}</span>
                                  </div>
                                </div>

                                {/* Stats Column */}
                                <div className="space-y-2.5">
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Total Orders:</span>{" "}
                                    <span className="font-semibold text-right">{client.totalOrders ?? 0}</span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Avg Spend:</span>{" "}
                                    <span className="font-semibold text-green-600 text-right">{client.averageSpending ? `₹ ${Math.round(client.averageSpending)}` : "-"}</span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Last Order Amount:</span>{" "}
                                    <span className="font-semibold text-green-600 text-right">{client.lastOrderAmount != null ? `₹ ${Math.round(client.lastOrderAmount)}` : "-"}</span>
                                  </p>
                                  <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                    <span>Opening Balance:</span>{" "}
                                    <span className="font-semibold text-right">{client.openingBalance ? `₹ ${Number(client.openingBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${client.openingBalanceType === 'credit' ? 'Cr' : 'Dr'}` : "₹ 0.00"}</span>
                                  </p>
                                  {(client.hasPOSOrders || (client.loyaltyPoints && client.loyaltyPoints > 0) || (client.usedLoyaltyPoints && client.usedLoyaltyPoints > 0)) && (
                                    <>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Active Loyalty Points:</span>{" "}
                                        <span className="font-semibold text-orange-600 text-right">{client.loyaltyPoints ?? 0}</span>
                                      </p>
                                      <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                        <span>Redeemed Points:</span>{" "}
                                        <span className="font-semibold text-slate-500 text-right">{client.usedLoyaltyPoints ?? 0}</span>
                                      </p>
                                    </>
                                  )}
                                  {client.mostPurchasedProduct && (
                                    <p className="text-slate-600 flex justify-between border-b border-slate-100 pb-1">
                                      <span className="min-w-[100px]">Most Purchased:</span>{" "}
                                      <span className="font-semibold text-blue-600 text-right whitespace-normal break-words" title={client.mostPurchasedProduct.name}>
                                        {client.mostPurchasedProduct.name} ({client.mostPurchasedProduct.qty})
                                      </span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Right Column: Product Detail */}
                            <div className="flex-1 flex flex-col border-t lg:border-t-0 lg:border-l border-slate-200">
                              <div className="bg-slate-100/70 text-slate-600 px-4 py-2 border-y border-slate-200/60 font-bold uppercase text-[10px] tracking-wider">
                                Last Ordered Items
                              </div>
                              <div className="p-4 flex-1">
                                {client.lastOrderedItems?.length > 0 ? (
                                  <div className="divide-y divide-slate-100 flex flex-col -mx-4 -mt-4 -mb-4">
                                    {client.lastOrderedItems.map((item, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs py-2.5 px-4 hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center gap-3">
                                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                                          <p className="font-semibold text-slate-800">{item}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-slate-400 italic py-2">
                                    No items found in last order
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-10 text-center text-slate-500"
                  >
                    No clients found
                  </td>
                </tr>
              )}
            </tbody>
          </ThemedTable>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      <AddClient
        isOpen={isAddClientOpen}
        onClose={() => {
          setIsAddClientOpen(false);
          setIsEdit(false);
          setSelectedClient(null);
        }}
        onAddClient={handleAddClient}
        clientData={selectedClient}
        isEdit={isEdit}
      />

      <ConfirmDialog
        open={!!deleteClientObj}
        title="Delete Client"
        description={`Are you sure you want to delete ${deleteClientObj?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={isDeleting}
        onCancel={() => setDeleteClientObj(null)}
        onConfirm={confirmDeleteClient}
      />
    </div>
  );
};

export default Client;
